import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { getSubscriptionStatus } from '../services/subscription';
import { useAppStore } from '../stores/useAppStore';
import { useAuthStore } from '../stores/useAuthStore';

const AppContext = createContext(null);

const initialState = {
  blocklist: [],
  userStats: {
    totalReports: 0,
    tier: 'scout',
    reputationScore: 0.3,
    helpedCount: 0,
  },
  subscription: {
    tier: 'free',
    status: null,
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
    provider: null,
    loading: true,
  },
};

function appReducer(state, action) {
  switch (action.type) {
    case 'SET_BLOCKLIST':
      return { ...state, blocklist: action.payload };
    case 'ADD_TO_BLOCKLIST':
      return { ...state, blocklist: [action.payload, ...state.blocklist] };
    case 'REMOVE_FROM_BLOCKLIST':
      return { ...state, blocklist: state.blocklist.filter((item) => item.number !== action.payload) };
    case 'INCREMENT_REPORTS':
      return {
        ...state,
        userStats: {
          ...state.userStats,
          totalReports: state.userStats.totalReports + 1,
          helpedCount: state.userStats.helpedCount + (action.payload || 0),
          tier: getTier(state.userStats.totalReports + 1),
        },
      };
    case 'SET_USER_STATS':
      return { ...state, userStats: { ...state.userStats, ...action.payload } };
    case 'SET_SUBSCRIPTION':
      return { ...state, subscription: { ...state.subscription, ...action.payload, loading: false } };
    case 'SET_SUBSCRIPTION_LOADING':
      return { ...state, subscription: { ...state.subscription, loading: action.payload } };
    default:
      return state;
  }
}

function getTier(reports) {
  if (reports >= 100) return 'champion';
  if (reports >= 26) return 'sentinel';
  if (reports >= 6) return 'guardian';
  return 'scout';
}

export function AppProvider({ children }) {
  const [internal, dispatch] = useReducer(appReducer, initialState);

  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const authLoading = useAuthStore((s) => s.authLoading);
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const nightMode = useAppStore((s) => s.nightMode);
  const toggleNightMode = useAppStore((s) => s.toggleNightMode);
  const recentLookups = useAppStore((s) => s.recentLookups);
  const addRecentLookup = useAppStore((s) => s.addRecentLookup);

  const toggleTheme = useCallback(() => toggleNightMode(), [toggleNightMode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await getCurrentUser();
        let attrs = {};
        try { attrs = await fetchUserAttributes(); } catch { /* ok */ }
        if (!cancelled) {
          setAuth({
            userId: u.userId,
            username: u.username,
            email: attrs.preferred_username || attrs.email || u.signInDetails?.loginId,
            name: attrs.name || '',
            preferredUsername: attrs.preferred_username || '',
          });
        }
      } catch {
        if (!cancelled) clearAuth();
      }
    })();
    return () => { cancelled = true; };
  }, [setAuth, clearAuth]);

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        getCurrentUser().then(async (u) => {
          let attrs = {};
          try { attrs = await fetchUserAttributes(); } catch { /* ok */ }
          setAuth({
            userId: u.userId,
            username: u.username,
            email: attrs.preferred_username || attrs.email || u.signInDetails?.loginId,
            name: attrs.name || '',
            preferredUsername: attrs.preferred_username || '',
          });
        }).catch(() => clearAuth());
      } else if (payload.event === 'signedOut') {
        clearAuth();
      }
    });
    return () => unsubscribe();
  }, [setAuth, clearAuth]);

  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch({ type: 'SET_SUBSCRIPTION_LOADING', payload: true });
      getSubscriptionStatus().then((sub) => {
        dispatch({ type: 'SET_SUBSCRIPTION', payload: sub });
      }).catch(() => dispatch({ type: 'SET_SUBSCRIPTION_LOADING', payload: false }));
    } else if (!authLoading) {
      dispatch({
        type: 'SET_SUBSCRIPTION',
        payload: { tier: 'free', status: null, currentPeriodEnd: null, cancelAtPeriodEnd: false, provider: null },
      });
    }
  }, [isAuthenticated, user, authLoading]);

  const state = {
    user,
    isAuthenticated,
    authLoading,
    theme: nightMode ? 'dark' : 'light',
    nightMode,
    blocklist: internal.blocklist,
    userStats: internal.userStats,
    subscription: internal.subscription,
    recentLookups,
  };

  const dispatchProxy = (action) => {
    if (action.type === 'TOGGLE_THEME') return toggleNightMode();
    if (action.type === 'ADD_LOOKUP') return addRecentLookup(action.payload);
    return dispatch(action);
  };

  return (
    <AppContext.Provider value={{ state, dispatch: dispatchProxy, toggleTheme }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}
